/**
 * Demo Mode Mock Data
 *
 * Curated mock data for screenshot and marketing purposes.
 * This data is designed to look realistic and showcase the application's capabilities.
 */

import { dateToTimestamp } from '@/lib/date-utils'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import {
  InquiryStageEnum,
  QuoteStatusEnum,
  InvoiceStatusEnum,
  BookingStageEnum,
  EventStageEnum,
  PaymentScheduleStatusEnum,
  TaskStatusEnum,
  ParentTypeEnum,
  FeedbackStageEnum,
} from '@/gen/cortado/v1/settings_pb'
import {
  WorkflowActionType,
  WorkflowConditionLogic,
  WorkflowConditionOperator,
  WorkflowTriggerEntityType,
  WorkflowTriggerEventType,
} from '@/gen/cortado/v1/cortado_pb'

// Helper to create timestamp relative to now
function daysFromNow(days: number): Timestamp {
  const date = new Date()
  date.setDate(date.getDate() + days)
  // Safe to assert non-null since we're creating a valid date
  return dateToTimestamp(date)!
}

function daysAgo(days: number): Timestamp {
  return daysFromNow(-days)
}

// ============================================================================
// Demo Organization & User
// ============================================================================

export const demoOrganization = {
  id: 'demo-org-001',
  name: 'Sunrise Event Co.',
  logoUrl: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=200&h=200&fit=crop',
  websiteUrl: 'https://sunriseeventco.com',
  contactEmail: 'hello@sunriseeventco.com',
  address: '123 Main Street, Austin, TX 78701',
  stripeAccountId: 'acct_demo123',
  stripeAccountType: 'standard',
  applicationFee: 2.9,
  timeSettings: {
    $typeName: 'cortado.v1.OrganizationTimeSettings',
    prepTime: 30,
    setupTime: 60,
    teardownTime: 30,
  },
  enabledFeatures: [],
  createdAt: daysAgo(365),
  updatedAt: daysAgo(1),
}

export const demoWorkflowDefinitions = [
  {
    id: 'demo-workflow-lead-follow-up',
    organizationId: demoOrganization.id,
    name: 'New lead follow-up',
    description: 'Send a tailored response and create a staff task for qualified inquiries.',
    enabled: true,
    trigger: {
      eventType: WorkflowTriggerEventType.INQUIRY_CREATED,
      entityType: WorkflowTriggerEntityType.INQUIRY,
    },
    conditions: {
      kind: {
        case: 'group' as const,
        value: {
          logic: WorkflowConditionLogic.AND,
          conditions: [
            {
              kind: {
                case: 'leaf' as const,
                value: {
                  field: 'event_guests',
                  operator: WorkflowConditionOperator.GREATER_THAN,
                  value: '50',
                },
              },
            },
            {
              kind: {
                case: 'leaf' as const,
                value: {
                  field: 'event_type',
                  operator: WorkflowConditionOperator.NOT_EQUALS,
                  value: 'Other',
                },
              },
            },
          ],
        },
      },
    },
    actions: [
      {
        actionType: WorkflowActionType.SEND_TEMPLATED_EMAIL,
        ordering: 1,
        config: '{"template":"lead_follow_up"}',
      },
      {
        actionType: WorkflowActionType.CREATE_TASK,
        ordering: 2,
        config: '{"title":"Review qualified inquiry"}',
      },
    ],
    stock: false,
    createdAt: daysAgo(42),
    updatedAt: daysAgo(3),
    createdBy: 'demo-user-001',
    updatedBy: 'demo-user-001',
    lastRunAt: daysAgo(1),
  },
  {
    id: 'demo-workflow-invoice-reminder',
    organizationId: demoOrganization.id,
    name: 'Invoice payment reminder',
    description: 'Notify the team when invoice details change before an event.',
    enabled: true,
    trigger: {
      eventType: WorkflowTriggerEventType.INVOICE_SENT,
      entityType: WorkflowTriggerEntityType.INVOICE,
    },
    conditions: {
      kind: {
        case: 'leaf' as const,
        value: {
          field: 'payment_status',
          operator: WorkflowConditionOperator.NOT_EQUALS,
          value: 'Paid',
        },
      },
    },
    actions: [
      {
        actionType: WorkflowActionType.CREATE_TASK,
        ordering: 1,
        config: '{"title":"Follow up on unpaid invoice"}',
      },
    ],
    stock: true,
    createdAt: daysAgo(90),
    updatedAt: daysAgo(7),
    createdBy: 'system',
    updatedBy: 'system',
    lastRunAt: daysAgo(4),
  },
  {
    id: 'demo-workflow-contact-cleanup',
    organizationId: demoOrganization.id,
    name: 'Contact cleanup',
    description: 'Keep customer records tidy when contacts are updated.',
    enabled: false,
    trigger: {
      eventType: WorkflowTriggerEventType.CONTACT_CREATED,
      entityType: WorkflowTriggerEntityType.CONTACT,
    },
    conditions: {
      kind: { case: undefined },
    },
    actions: [
      {
        actionType: WorkflowActionType.UPDATE_FIELD,
        ordering: 1,
        config: '{"field":"source","value":"Manual review"}',
      },
    ],
    stock: false,
    createdAt: daysAgo(12),
    updatedAt: daysAgo(12),
    createdBy: 'demo-user-001',
    updatedBy: 'demo-user-001',
  },
]

export const demoUser = {
  id: 'demo-user-001',
  email: 'alex@sunriseeventco.com',
  firstName: 'Alex',
  lastName: 'Morgan',
  phoneNumber: '+1 (512) 555-0123',
  createdAt: daysAgo(365),
  updatedAt: daysAgo(1),
  createdBy: 'system',
  updatedBy: 'system',
}

export const demoOrganizationUser = {
  organizationId: demoOrganization.id,
  user: demoUser,
  title: 'Owner',
  email: demoUser.email,
  firstName: demoUser.firstName,
  lastName: demoUser.lastName,
  createdAt: daysAgo(365),
  updatedAt: daysAgo(1),
  roles: [
    {
      id: 'role-admin',
      name: 'Admin',
      organizationId: demoOrganization.id,
    },
  ],
}

export const demoUser2 = {
  id: 'demo-user-002',
  email: 'sarah.johnson@sunriseeventco.com',
  firstName: 'Sarah',
  lastName: 'Johnson',
  phoneNumber: '+1 (512) 555-0124',
  createdAt: daysAgo(180),
  updatedAt: daysAgo(5),
  createdBy: demoUser.id,
  updatedBy: demoUser.id,
}

export const demoUser3 = {
  id: 'demo-user-003',
  email: 'mike.davis@sunriseeventco.com',
  firstName: 'Mike',
  lastName: 'Davis',
  phoneNumber: '+1 (512) 555-0125',
  createdAt: daysAgo(120),
  updatedAt: daysAgo(3),
  createdBy: demoUser.id,
  updatedBy: demoUser.id,
}

export const demoUser4 = {
  id: 'demo-user-004',
  email: 'lisa.chen@sunriseeventco.com',
  firstName: 'Lisa',
  lastName: 'Chen',
  phoneNumber: '+1 (512) 555-0126',
  createdAt: daysAgo(90),
  updatedAt: daysAgo(2),
  createdBy: demoUser.id,
  updatedBy: demoUser.id,
}

export const demoOrganizationUsers = [
  demoOrganizationUser,
  {
    organizationId: demoOrganization.id,
    user: demoUser2,
    title: 'Event Coordinator',
    email: demoUser2.email,
    firstName: demoUser2.firstName,
    lastName: demoUser2.lastName,
    createdAt: daysAgo(180),
    updatedAt: daysAgo(5),
    roles: [
      {
        id: 'role-staff',
        name: 'Staff',
        organizationId: demoOrganization.id,
      },
    ],
  },
  {
    organizationId: demoOrganization.id,
    user: demoUser3,
    title: 'Operations Manager',
    email: demoUser3.email,
    firstName: demoUser3.firstName,
    lastName: demoUser3.lastName,
    createdAt: daysAgo(120),
    updatedAt: daysAgo(3),
    roles: [
      {
        id: 'role-admin',
        name: 'Admin',
        organizationId: demoOrganization.id,
      },
    ],
  },
  {
    organizationId: demoOrganization.id,
    user: demoUser4,
    title: 'Sales Associate',
    email: demoUser4.email,
    firstName: demoUser4.firstName,
    lastName: demoUser4.lastName,
    createdAt: daysAgo(90),
    updatedAt: daysAgo(2),
    roles: [
      {
        id: 'role-staff',
        name: 'Staff',
        organizationId: demoOrganization.id,
      },
    ],
  },
]

// ============================================================================
// Demo Contacts
// ============================================================================

export const demoContacts = [
  {
    id: 'contact-001',
    organizationId: demoOrganization.id,
    firstName: 'Sarah',
    lastName: 'Thompson',
    email: 'sarah.thompson@techventures.io',
    phoneNumber: '+1 (512) 555-1234',
    createdAt: daysAgo(45),
    updatedAt: daysAgo(2),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'contact-002',
    organizationId: demoOrganization.id,
    firstName: 'Michael',
    lastName: 'Chen',
    email: 'michael.chen@gmail.com',
    phoneNumber: '+1 (512) 555-2345',
    createdAt: daysAgo(30),
    updatedAt: daysAgo(5),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'contact-003',
    organizationId: demoOrganization.id,
    firstName: 'Emily',
    lastName: 'Rodriguez',
    email: 'emily.r@innovatecorp.com',
    phoneNumber: '+1 (512) 555-3456',
    createdAt: daysAgo(60),
    updatedAt: daysAgo(10),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'contact-004',
    organizationId: demoOrganization.id,
    firstName: 'James',
    lastName: 'Wilson',
    email: 'jwilson@wilsonenterprises.com',
    phoneNumber: '+1 (512) 555-4567',
    createdAt: daysAgo(90),
    updatedAt: daysAgo(15),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'contact-005',
    organizationId: demoOrganization.id,
    firstName: 'Jessica',
    lastName: 'Park',
    email: 'jessica.park@outlook.com',
    phoneNumber: '+1 (512) 555-5678',
    createdAt: daysAgo(20),
    updatedAt: daysAgo(1),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'contact-006',
    organizationId: demoOrganization.id,
    firstName: 'David',
    lastName: 'Martinez',
    email: 'david.m@startupco.com',
    phoneNumber: '+1 (512) 555-6789',
    createdAt: daysAgo(25),
    updatedAt: daysAgo(3),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'contact-007',
    organizationId: demoOrganization.id,
    firstName: 'Lisa',
    lastName: 'Anderson',
    email: 'lisa.anderson@corporateevents.com',
    phoneNumber: '+1 (512) 555-7890',
    createdAt: daysAgo(70),
    updatedAt: daysAgo(12),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'contact-008',
    organizationId: demoOrganization.id,
    firstName: 'Robert',
    lastName: 'Kim',
    email: 'rkim@techstartup.io',
    phoneNumber: '+1 (512) 555-8901',
    createdAt: daysAgo(15),
    updatedAt: daysAgo(2),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'contact-009',
    organizationId: demoOrganization.id,
    firstName: 'Amanda',
    lastName: 'Taylor',
    email: 'amanda.taylor@weddingplanners.com',
    phoneNumber: '+1 (512) 555-9012',
    createdAt: daysAgo(50),
    updatedAt: daysAgo(8),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'contact-010',
    organizationId: demoOrganization.id,
    firstName: 'Christopher',
    lastName: 'Brown',
    email: 'chris.brown@eventpros.net',
    phoneNumber: '+1 (512) 555-0124',
    createdAt: daysAgo(35),
    updatedAt: daysAgo(6),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'contact-011',
    organizationId: demoOrganization.id,
    firstName: 'Maria',
    lastName: 'Garcia',
    email: 'maria.garcia@familyevents.com',
    phoneNumber: '+1 (512) 555-1235',
    createdAt: daysAgo(80),
    updatedAt: daysAgo(20),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'contact-012',
    organizationId: demoOrganization.id,
    firstName: 'Daniel',
    lastName: 'Lee',
    email: 'daniel.lee@corporate.com',
    phoneNumber: '+1 (512) 555-2346',
    createdAt: daysAgo(10),
    updatedAt: daysAgo(0),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'contact-013',
    organizationId: demoOrganization.id,
    firstName: 'Jennifer',
    lastName: 'White',
    email: 'jennifer.white@gmail.com',
    phoneNumber: '+1 (512) 555-3457',
    createdAt: daysAgo(55),
    updatedAt: daysAgo(9),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'contact-014',
    organizationId: demoOrganization.id,
    firstName: 'Thomas',
    lastName: 'Harris',
    email: 'thomas.harris@bigcorp.com',
    phoneNumber: '+1 (512) 555-4568',
    createdAt: daysAgo(40),
    updatedAt: daysAgo(7),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'contact-015',
    organizationId: demoOrganization.id,
    firstName: 'Nicole',
    lastName: 'Clark',
    email: 'nicole.clark@eventsolutions.com',
    phoneNumber: '+1 (512) 555-5679',
    createdAt: daysAgo(18),
    updatedAt: daysAgo(4),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
]

// ============================================================================
// Demo Inquiries (Leads)
// ============================================================================

export const demoInquiries = [
  {
    id: 'inquiry-001',
    organizationId: demoOrganization.id,
    contactId: demoContacts[0].id,
    stage: InquiryStageEnum.INQUIRY_STAGE_NEW,
    eventType: 'Corporate',
    eventDate: daysFromNow(45),
    eventDays: 1,
    eventLength: 4,
    eventLocation: 'Downtown Convention Center',
    eventGuests: 150,
    eventTimezone: 'America/Chicago',
    referrer: 'Google Search',
    createdAt: daysAgo(2),
    updatedAt: daysAgo(1),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[0],
    quoteTotal: 0,
    notesCount: 2,
    tasksCount: 1,
    quotesCount: 0,
    itemsCount: 0,
  },
  {
    id: 'inquiry-002',
    organizationId: demoOrganization.id,
    contactId: demoContacts[1].id,
    stage: InquiryStageEnum.INQUIRY_STAGE_IN_PROGRESS,
    eventType: 'Wedding',
    eventDate: daysFromNow(120),
    eventDays: 1,
    eventLength: 8,
    eventLocation: 'Lakeside Manor',
    eventGuests: 200,
    eventTimezone: 'America/Chicago',
    referrer: 'Referral',
    createdAt: daysAgo(15),
    updatedAt: daysAgo(1),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[1],
    quoteTotal: 15500,
    notesCount: 5,
    tasksCount: 3,
    quotesCount: 1,
    itemsCount: 8,
  },
  {
    id: 'inquiry-003',
    organizationId: demoOrganization.id,
    contactId: demoContacts[2].id,
    stage: InquiryStageEnum.INQUIRY_STAGE_QUOTE_SENT,
    eventType: 'Corporate',
    eventDate: daysFromNow(30),
    eventDays: 2,
    eventLength: 6,
    eventLocation: 'Innovate Corp HQ',
    eventGuests: 75,
    eventTimezone: 'America/Chicago',
    referrer: 'Website',
    createdAt: daysAgo(20),
    updatedAt: daysAgo(3),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[2],
    quoteTotal: 8750,
    notesCount: 3,
    tasksCount: 2,
    quotesCount: 1,
    itemsCount: 5,
  },
  {
    id: 'inquiry-004',
    organizationId: demoOrganization.id,
    contactId: demoContacts[3].id,
    stage: InquiryStageEnum.INQUIRY_STAGE_ACCEPTED,
    eventType: 'Party',
    eventDate: daysFromNow(14),
    eventDays: 1,
    eventLength: 5,
    eventLocation: 'Wilson Estate',
    eventGuests: 100,
    eventTimezone: 'America/Chicago',
    referrer: 'Instagram',
    createdAt: daysAgo(45),
    updatedAt: daysAgo(7),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[3],
    quoteTotal: 6500,
    notesCount: 8,
    tasksCount: 5,
    quotesCount: 2,
    itemsCount: 6,
    bookingId: 'booking-001',
  },
  {
    id: 'inquiry-005',
    organizationId: demoOrganization.id,
    contactId: demoContacts[4].id,
    stage: InquiryStageEnum.INQUIRY_STAGE_NEW,
    eventType: 'Party',
    eventDate: daysFromNow(60),
    eventDays: 1,
    eventLength: 3,
    eventLocation: 'TBD',
    eventGuests: 50,
    eventTimezone: 'America/Chicago',
    referrer: 'Facebook',
    createdAt: daysAgo(1),
    updatedAt: daysAgo(0),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[4],
    quoteTotal: 0,
    notesCount: 1,
    tasksCount: 0,
    quotesCount: 0,
    itemsCount: 0,
  },
  {
    id: 'inquiry-006',
    organizationId: demoOrganization.id,
    contactId: demoContacts[5].id,
    stage: InquiryStageEnum.INQUIRY_STAGE_IN_PROGRESS,
    eventType: 'Corporate',
    eventDate: daysFromNow(90),
    eventDays: 1,
    eventLength: 6,
    eventLocation: 'StartupCo Office',
    eventGuests: 120,
    eventTimezone: 'America/Chicago',
    referrer: 'LinkedIn',
    createdAt: daysAgo(8),
    updatedAt: daysAgo(2),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[5],
    quoteTotal: 7200,
    notesCount: 4,
    tasksCount: 2,
    quotesCount: 1,
    itemsCount: 6,
  },
  {
    id: 'inquiry-007',
    organizationId: demoOrganization.id,
    contactId: demoContacts[6].id,
    stage: InquiryStageEnum.INQUIRY_STAGE_QUOTE_SENT,
    eventType: 'Corporate',
    eventDate: daysFromNow(25),
    eventDays: 1,
    eventLength: 5,
    eventLocation: 'Corporate Events Hall',
    eventGuests: 180,
    eventTimezone: 'America/Chicago',
    referrer: 'Website',
    createdAt: daysAgo(12),
    updatedAt: daysAgo(4),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[6],
    quoteTotal: 10800,
    notesCount: 3,
    tasksCount: 1,
    quotesCount: 1,
    itemsCount: 7,
  },
  {
    id: 'inquiry-008',
    organizationId: demoOrganization.id,
    contactId: demoContacts[7].id,
    stage: InquiryStageEnum.INQUIRY_STAGE_NEW,
    eventType: 'Wedding',
    eventDate: daysFromNow(150),
    eventDays: 1,
    eventLength: 7,
    eventLocation: 'TBD',
    eventGuests: 175,
    eventTimezone: 'America/Chicago',
    referrer: 'Instagram',
    createdAt: daysAgo(3),
    updatedAt: daysAgo(1),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[7],
    quoteTotal: 0,
    notesCount: 1,
    tasksCount: 1,
    quotesCount: 0,
    itemsCount: 0,
  },
  {
    id: 'inquiry-009',
    organizationId: demoOrganization.id,
    contactId: demoContacts[8].id,
    stage: InquiryStageEnum.INQUIRY_STAGE_ACCEPTED,
    eventType: 'Wedding',
    eventDate: daysFromNow(100),
    eventDays: 1,
    eventLength: 8,
    eventLocation: 'Garden Venue',
    eventGuests: 150,
    eventTimezone: 'America/Chicago',
    referrer: 'Referral',
    createdAt: daysAgo(50),
    updatedAt: daysAgo(10),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[8],
    quoteTotal: 11250,
    notesCount: 6,
    tasksCount: 4,
    quotesCount: 2,
    itemsCount: 9,
    bookingId: 'booking-002',
  },
  {
    id: 'inquiry-010',
    organizationId: demoOrganization.id,
    contactId: demoContacts[9].id,
    stage: InquiryStageEnum.INQUIRY_STAGE_REJECTED,
    eventType: 'Corporate',
    eventDate: daysFromNow(20),
    eventDays: 1,
    eventLength: 4,
    eventLocation: 'Event Pros Center',
    eventGuests: 90,
    eventTimezone: 'America/Chicago',
    referrer: 'Google Search',
    createdAt: daysAgo(30),
    updatedAt: daysAgo(15),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[9],
    quoteTotal: 5400,
    notesCount: 2,
    tasksCount: 0,
    quotesCount: 1,
    itemsCount: 4,
  },
  {
    id: 'inquiry-011',
    organizationId: demoOrganization.id,
    contactId: demoContacts[10].id,
    stage: InquiryStageEnum.INQUIRY_STAGE_IN_PROGRESS,
    eventType: 'Party',
    eventDate: daysFromNow(35),
    eventDays: 1,
    eventLength: 4,
    eventLocation: 'Family Events Venue',
    eventGuests: 80,
    eventTimezone: 'America/Chicago',
    referrer: 'Facebook',
    createdAt: daysAgo(18),
    updatedAt: daysAgo(5),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[10],
    quoteTotal: 4800,
    notesCount: 3,
    tasksCount: 2,
    quotesCount: 1,
    itemsCount: 5,
  },
  {
    id: 'inquiry-012',
    organizationId: demoOrganization.id,
    contactId: demoContacts[11].id,
    stage: InquiryStageEnum.INQUIRY_STAGE_NEW,
    eventType: 'Corporate',
    eventDate: daysFromNow(70),
    eventDays: 2,
    eventLength: 8,
    eventLocation: 'Corporate HQ',
    eventGuests: 200,
    eventTimezone: 'America/Chicago',
    referrer: 'Website',
    createdAt: daysAgo(0),
    updatedAt: daysAgo(0),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[11],
    quoteTotal: 0,
    notesCount: 0,
    tasksCount: 0,
    quotesCount: 0,
    itemsCount: 0,
  },
  {
    id: 'inquiry-013',
    organizationId: demoOrganization.id,
    contactId: demoContacts[12].id,
    stage: InquiryStageEnum.INQUIRY_STAGE_QUOTE_SENT,
    eventType: 'Wedding',
    eventDate: daysFromNow(180),
    eventDays: 1,
    eventLength: 9,
    eventLocation: 'Wedding Venue',
    eventGuests: 250,
    eventTimezone: 'America/Chicago',
    referrer: 'Instagram',
    createdAt: daysAgo(25),
    updatedAt: daysAgo(7),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[12],
    quoteTotal: 18750,
    notesCount: 5,
    tasksCount: 3,
    quotesCount: 1,
    itemsCount: 10,
  },
]

// ============================================================================
// Demo Bookings
// ============================================================================

export const demoBookings = [
  {
    id: 'booking-001',
    organizationId: demoOrganization.id,
    inquiryId: 'inquiry-004',
    contactId: demoContacts[3].id,
    stage: BookingStageEnum.BOOKING_STAGE_UPCOMING,
    eventType: 'Party',
    eventLocation: 'Wilson Estate',
    eventState: 'TX',
    eventCounty: 'Travis',
    createdAt: daysAgo(38),
    updatedAt: daysAgo(7),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    totalInvoiceAmount: 6500,
    totalPaid: 3250,
    totalDue: 3250,
    eventsCount: 1,
    invoicesCount: 1,
    notesCount: 5,
    tasksCount: 3,
  },
  {
    id: 'booking-002',
    organizationId: demoOrganization.id,
    inquiryId: 'inquiry-009',
    contactId: demoContacts[8].id,
    stage: BookingStageEnum.BOOKING_STAGE_UPCOMING,
    eventType: 'Wedding',
    eventLocation: 'Garden Venue',
    eventState: 'TX',
    eventCounty: 'Travis',
    createdAt: daysAgo(45),
    updatedAt: daysAgo(10),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    totalInvoiceAmount: 11250,
    totalPaid: 5625,
    totalDue: 5625,
    eventsCount: 1,
    invoicesCount: 1,
    notesCount: 6,
    tasksCount: 4,
  },
  {
    id: 'booking-003',
    organizationId: demoOrganization.id,
    inquiryId: 'inquiry-002',
    contactId: demoContacts[1].id,
    stage: BookingStageEnum.BOOKING_STAGE_UPCOMING,
    eventType: 'Wedding',
    eventLocation: 'Lakeside Manor',
    eventState: 'TX',
    eventCounty: 'Travis',
    createdAt: daysAgo(10),
    updatedAt: daysAgo(2),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    totalInvoiceAmount: 15500,
    totalPaid: 0,
    totalDue: 15500,
    eventsCount: 0,
    invoicesCount: 0,
    notesCount: 5,
    tasksCount: 3,
  },
  {
    id: 'booking-004',
    organizationId: demoOrganization.id,
    inquiryId: 'inquiry-003',
    contactId: demoContacts[2].id,
    stage: BookingStageEnum.BOOKING_STAGE_UPCOMING,
    eventType: 'Corporate',
    eventLocation: 'Innovate Corp HQ',
    eventState: 'TX',
    eventCounty: 'Travis',
    createdAt: daysAgo(15),
    updatedAt: daysAgo(3),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    totalInvoiceAmount: 8750,
    totalPaid: 8750,
    totalDue: 0,
    eventsCount: 1,
    invoicesCount: 1,
    notesCount: 3,
    tasksCount: 2,
  },
  {
    id: 'booking-005',
    organizationId: demoOrganization.id,
    inquiryId: 'inquiry-006',
    contactId: demoContacts[5].id,
    stage: BookingStageEnum.BOOKING_STAGE_COMPLETE,
    eventType: 'Corporate',
    eventLocation: 'StartupCo Office',
    eventState: 'TX',
    eventCounty: 'Travis',
    createdAt: daysAgo(60),
    updatedAt: daysAgo(5),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    totalInvoiceAmount: 7200,
    totalPaid: 7200,
    totalDue: 0,
    eventsCount: 1,
    invoicesCount: 1,
    notesCount: 4,
    tasksCount: 0,
  },
]

// ============================================================================
// Demo Events
// ============================================================================

export const demoEvents = [
  {
    id: 'event-001',
    organizationId: demoOrganization.id,
    contactId: demoContacts[3].id,
    bookingId: 'booking-001',
    stage: EventStageEnum.EVENT_STAGE_UPCOMING,
    eventDate: daysFromNow(14),
    eventLength: 5,
    eventGuests: 100,
    eventTimezone: 'America/Chicago',
    createdAt: daysAgo(38),
    updatedAt: daysAgo(7),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[3],
    scheduledUsersCount: 3,
  },
  {
    id: 'event-002',
    organizationId: demoOrganization.id,
    contactId: demoContacts[0].id,
    bookingId: 'booking-002',
    stage: EventStageEnum.EVENT_STAGE_COMPLETE,
    eventDate: daysAgo(7),
    eventLength: 4,
    eventGuests: 80,
    eventTimezone: 'America/Chicago',
    createdAt: daysAgo(60),
    updatedAt: daysAgo(7),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[0],
    scheduledUsersCount: 2,
  },
  {
    id: 'event-003',
    organizationId: demoOrganization.id,
    contactId: demoContacts[8].id,
    bookingId: 'booking-002',
    stage: EventStageEnum.EVENT_STAGE_UPCOMING,
    eventDate: daysFromNow(3),
    eventLength: 6,
    eventGuests: 150,
    eventTimezone: 'America/Chicago',
    createdAt: daysAgo(45),
    updatedAt: daysAgo(10),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[8],
    scheduledUsersCount: 4,
  },
  {
    id: 'event-004',
    organizationId: demoOrganization.id,
    contactId: demoContacts[2].id,
    bookingId: 'booking-004',
    stage: EventStageEnum.EVENT_STAGE_UPCOMING,
    eventDate: daysFromNow(5),
    eventLength: 6,
    eventGuests: 75,
    eventTimezone: 'America/Chicago',
    createdAt: daysAgo(15),
    updatedAt: daysAgo(3),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[2],
    scheduledUsersCount: 2,
  },
  {
    id: 'event-005',
    organizationId: demoOrganization.id,
    contactId: demoContacts[5].id,
    bookingId: 'booking-005',
    stage: EventStageEnum.EVENT_STAGE_COMPLETE,
    eventDate: daysAgo(14),
    eventLength: 4,
    eventGuests: 120,
    eventTimezone: 'America/Chicago',
    createdAt: daysAgo(60),
    updatedAt: daysAgo(5),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[5],
    scheduledUsersCount: 3,
  },
  {
    id: 'event-006',
    organizationId: demoOrganization.id,
    contactId: demoContacts[1].id,
    bookingId: 'booking-003',
    stage: EventStageEnum.EVENT_STAGE_UPCOMING,
    eventDate: daysFromNow(7),
    eventLength: 8,
    eventGuests: 200,
    eventTimezone: 'America/Chicago',
    createdAt: daysAgo(10),
    updatedAt: daysAgo(2),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[1],
    scheduledUsersCount: 5,
  },
  {
    id: 'event-007',
    organizationId: demoOrganization.id,
    contactId: demoContacts[6].id,
    inquiryId: 'inquiry-007',
    stage: EventStageEnum.EVENT_STAGE_UPCOMING,
    eventDate: daysFromNow(10),
    eventLength: 5,
    eventGuests: 180,
    eventTimezone: 'America/Chicago',
    createdAt: daysAgo(12),
    updatedAt: daysAgo(4),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[6],
    scheduledUsersCount: 4,
  },
  {
    id: 'event-008',
    organizationId: demoOrganization.id,
    contactId: demoContacts[0].id,
    inquiryId: 'inquiry-001',
    stage: EventStageEnum.EVENT_STAGE_UPCOMING,
    eventDate: daysFromNow(1),
    eventLength: 4,
    eventGuests: 150,
    eventTimezone: 'America/Chicago',
    createdAt: daysAgo(2),
    updatedAt: daysAgo(1),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[0],
    scheduledUsersCount: 3,
  },
  {
    id: 'event-009',
    organizationId: demoOrganization.id,
    contactId: demoContacts[10].id,
    inquiryId: 'inquiry-011',
    stage: EventStageEnum.EVENT_STAGE_UPCOMING,
    eventDate: daysFromNow(0),
    eventLength: 4,
    eventGuests: 80,
    eventTimezone: 'America/Chicago',
    createdAt: daysAgo(18),
    updatedAt: daysAgo(5),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    contact: demoContacts[10],
    scheduledUsersCount: 2,
  },
]

// ============================================================================
// Demo Invoices
// ============================================================================

export const demoInvoices = [
  {
    id: 'invoice-001',
    organizationId: demoOrganization.id,
    bookingId: 'booking-001',
    quoteId: 'quote-001',
    contactId: demoContacts[3].id,
    invoiceNumber: 1001,
    currentRevisionId: 'inv-rev-001',
    title: 'Wilson Family Celebration',
    description: 'Full service catering for anniversary party',
    subtotalCents: BigInt(595000),
    salesTaxCents: BigInt(55000),
    discountCents: BigInt(0),
    totalCents: BigInt(650000),
    amountPaidCents: BigInt(325000),
    amountDueCents: BigInt(325000),
    status: InvoiceStatusEnum.INVOICE_STATUS_PARTIALLY_PAID,
    isLocked: true,
    sentAt: daysAgo(30),
    createdAt: daysAgo(38),
    updatedAt: daysAgo(14),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    lineItems: [
      {
        id: 'line-001',
        invoiceRevisionId: 'inv-rev-001',
        name: 'Premium Catering Package',
        description: 'Full service catering for 100 guests',
        unitPriceCents: BigInt(4500),
        quantity: 100,
        unit: 'per guest',
        lineTotalCents: BigInt(450000),
        sortOrder: 1,
        isTaxable: true,
        salesTaxRate: 8.25,
        salesTaxCents: BigInt(37125),
      },
      {
        id: 'line-002',
        invoiceRevisionId: 'inv-rev-001',
        name: 'Bar Service',
        description: 'Open bar with premium selections',
        unitPriceCents: BigInt(2500),
        quantity: 5,
        unit: 'per hour',
        lineTotalCents: BigInt(125000),
        sortOrder: 2,
        isTaxable: true,
        salesTaxRate: 8.25,
        salesTaxCents: BigInt(10313),
      },
      {
        id: 'line-003',
        invoiceRevisionId: 'inv-rev-001',
        name: 'Event Coordination',
        description: 'On-site event coordinator',
        unitPriceCents: BigInt(20000),
        quantity: 1,
        unit: 'flat rate',
        lineTotalCents: BigInt(20000),
        sortOrder: 3,
        isTaxable: true,
        salesTaxRate: 8.25,
        salesTaxCents: BigInt(1650),
      },
    ],
    paymentSchedules: [
      {
        id: 'schedule-001',
        organizationId: demoOrganization.id,
        invoiceId: 'invoice-001',
        invoiceRevisionId: 'inv-rev-001',
        sequenceNumber: 1,
        description: '50% Deposit',
        dueDate: daysAgo(30),
        amountCents: BigInt(325000),
        amountPaidCents: BigInt(325000),
        status: PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PAID,
        createdAt: daysAgo(38),
        updatedAt: daysAgo(14),
      },
      {
        id: 'schedule-002',
        organizationId: demoOrganization.id,
        invoiceId: 'invoice-001',
        invoiceRevisionId: 'inv-rev-001',
        sequenceNumber: 2,
        description: 'Final Payment',
        dueDate: daysFromNow(7),
        amountCents: BigInt(325000),
        amountPaidCents: BigInt(0),
        status: PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PENDING,
        createdAt: daysAgo(38),
        updatedAt: daysAgo(38),
      },
    ],
  },
  {
    id: 'invoice-002',
    organizationId: demoOrganization.id,
    bookingId: 'booking-002',
    contactId: demoContacts[0].id,
    invoiceNumber: 1000,
    currentRevisionId: 'inv-rev-002',
    title: 'Tech Ventures Annual Meeting',
    description: 'Corporate catering services',
    subtotalCents: BigInt(420000),
    salesTaxCents: BigInt(34650),
    discountCents: BigInt(0),
    totalCents: BigInt(454650),
    amountPaidCents: BigInt(454650),
    amountDueCents: BigInt(0),
    status: InvoiceStatusEnum.INVOICE_STATUS_PAID,
    isLocked: true,
    sentAt: daysAgo(45),
    createdAt: daysAgo(60),
    updatedAt: daysAgo(7),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    lineItems: [],
    paymentSchedules: [],
  },
  {
    id: 'invoice-003',
    organizationId: demoOrganization.id,
    bookingId: 'booking-002',
    contactId: demoContacts[8].id,
    invoiceNumber: 1002,
    currentRevisionId: 'inv-rev-003',
    title: 'Garden Venue Wedding',
    description: 'Wedding reception catering',
    subtotalCents: BigInt(1038750),
    salesTaxCents: BigInt(85687),
    discountCents: BigInt(0),
    totalCents: BigInt(1124437),
    amountPaidCents: BigInt(562219),
    amountDueCents: BigInt(562218),
    status: InvoiceStatusEnum.INVOICE_STATUS_PARTIALLY_PAID,
    isLocked: true,
    sentAt: daysAgo(40),
    createdAt: daysAgo(45),
    updatedAt: daysAgo(10),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    lineItems: [],
    paymentSchedules: [],
  },
  {
    id: 'invoice-004',
    organizationId: demoOrganization.id,
    bookingId: 'booking-004',
    contactId: demoContacts[2].id,
    invoiceNumber: 1003,
    currentRevisionId: 'inv-rev-004',
    title: 'Innovate Corp Conference',
    description: 'Two-day corporate conference catering',
    subtotalCents: BigInt(805000),
    salesTaxCents: BigInt(66413),
    discountCents: BigInt(0),
    totalCents: BigInt(871413),
    amountPaidCents: BigInt(871413),
    amountDueCents: BigInt(0),
    status: InvoiceStatusEnum.INVOICE_STATUS_PAID,
    isLocked: true,
    sentAt: daysAgo(20),
    createdAt: daysAgo(15),
    updatedAt: daysAgo(3),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    lineItems: [],
    paymentSchedules: [],
  },
  {
    id: 'invoice-005',
    organizationId: demoOrganization.id,
    contactId: demoContacts[6].id,
    invoiceNumber: 1004,
    currentRevisionId: 'inv-rev-005',
    title: 'Corporate Events Hall',
    description: 'Corporate event catering',
    subtotalCents: BigInt(990000),
    salesTaxCents: BigInt(81675),
    discountCents: BigInt(0),
    totalCents: BigInt(1071675),
    amountPaidCents: BigInt(0),
    amountDueCents: BigInt(1071675),
    status: InvoiceStatusEnum.INVOICE_STATUS_DRAFT,
    isLocked: false,
    createdAt: daysAgo(5),
    updatedAt: daysAgo(4),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    lineItems: [],
    paymentSchedules: [],
  },
  {
    id: 'invoice-006',
    organizationId: demoOrganization.id,
    contactId: demoContacts[9].id,
    invoiceNumber: 1005,
    currentRevisionId: 'inv-rev-006',
    title: 'Event Pros Center',
    description: 'Corporate event services',
    subtotalCents: BigInt(495000),
    salesTaxCents: BigInt(40838),
    discountCents: BigInt(0),
    totalCents: BigInt(535838),
    amountPaidCents: BigInt(0),
    amountDueCents: BigInt(535838),
    status: InvoiceStatusEnum.INVOICE_STATUS_SENT,
    isLocked: true,
    sentAt: daysAgo(25),
    createdAt: daysAgo(30),
    updatedAt: daysAgo(15),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    lineItems: [],
    paymentSchedules: [],
  },
  {
    id: 'invoice-007',
    organizationId: demoOrganization.id,
    contactId: demoContacts[11].id,
    invoiceNumber: 1006,
    currentRevisionId: 'inv-rev-007',
    title: 'Corporate HQ Event',
    description: 'Large corporate gathering',
    subtotalCents: BigInt(1800000),
    salesTaxCents: BigInt(148500),
    discountCents: BigInt(0),
    totalCents: BigInt(1948500),
    amountPaidCents: BigInt(0),
    amountDueCents: BigInt(1948500),
    status: InvoiceStatusEnum.INVOICE_STATUS_OVERDUE,
    isLocked: true,
    sentAt: daysAgo(45),
    createdAt: daysAgo(50),
    updatedAt: daysAgo(20),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    lineItems: [],
    paymentSchedules: [],
  },
  {
    id: 'invoice-008',
    organizationId: demoOrganization.id,
    contactId: demoContacts[4].id,
    invoiceNumber: 1007,
    currentRevisionId: 'inv-rev-008',
    title: 'Cancelled Party Event',
    description: 'Party event that was cancelled',
    subtotalCents: BigInt(450000),
    salesTaxCents: BigInt(37125),
    discountCents: BigInt(0),
    totalCents: BigInt(487125),
    amountPaidCents: BigInt(0),
    amountDueCents: BigInt(487125),
    status: InvoiceStatusEnum.INVOICE_STATUS_CANCELLED,
    isLocked: true,
    sentAt: daysAgo(60),
    createdAt: daysAgo(65),
    updatedAt: daysAgo(50),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
    lineItems: [],
    paymentSchedules: [],
  },
]

// ============================================================================
// Demo Quotes
// ============================================================================

export const demoQuotes = [
  {
    id: 'quote-001',
    organizationId: demoOrganization.id,
    inquiryId: 'inquiry-004',
    contactId: demoContacts[3].id,
    quoteNumber: 101,
    currentRevisionId: 'quote-rev-001',
    title: 'Wilson Family Celebration',
    description: 'Full service catering for anniversary party',
    validUntil: daysFromNow(30),
    subtotalCents: BigInt(595000),
    salesTaxCents: BigInt(55000),
    discountCents: BigInt(0),
    totalCents: BigInt(650000),
    status: QuoteStatusEnum.QUOTE_STATUS_ACCEPTED,
    sentAt: daysAgo(40),
    viewedAt: daysAgo(39),
    acceptedAt: daysAgo(38),
    eventDate: daysFromNow(14),
    eventLocation: 'Wilson Estate',
    guestCount: 100,
    eventDuration: 5,
    createdAt: daysAgo(45),
    updatedAt: daysAgo(38),
    updatedBy: demoUser.id,
    lineItems: [],
    paymentSchedules: [],
  },
  {
    id: 'quote-002',
    organizationId: demoOrganization.id,
    inquiryId: 'inquiry-002',
    contactId: demoContacts[1].id,
    quoteNumber: 102,
    currentRevisionId: 'quote-rev-002',
    title: 'Chen-Martinez Wedding',
    description: 'Wedding reception catering',
    validUntil: daysFromNow(14),
    subtotalCents: BigInt(1425000),
    salesTaxCents: BigInt(125000),
    discountCents: BigInt(0),
    totalCents: BigInt(1550000),
    status: QuoteStatusEnum.QUOTE_STATUS_SENT,
    sentAt: daysAgo(3),
    viewedAt: daysAgo(2),
    eventDate: daysFromNow(120),
    eventLocation: 'Lakeside Manor',
    guestCount: 200,
    eventDuration: 8,
    createdAt: daysAgo(5),
    updatedAt: daysAgo(3),
    updatedBy: demoUser.id,
    lineItems: [],
    paymentSchedules: [],
  },
  {
    id: 'quote-003',
    organizationId: demoOrganization.id,
    inquiryId: 'inquiry-003',
    contactId: demoContacts[2].id,
    quoteNumber: 103,
    currentRevisionId: 'quote-rev-003',
    title: 'Innovate Corp Conference',
    description: 'Two-day corporate conference catering',
    validUntil: daysFromNow(21),
    subtotalCents: BigInt(805000),
    salesTaxCents: BigInt(70000),
    discountCents: BigInt(0),
    totalCents: BigInt(875000),
    status: QuoteStatusEnum.QUOTE_STATUS_SENT,
    sentAt: daysAgo(5),
    eventDate: daysFromNow(30),
    eventLocation: 'Innovate Corp HQ',
    guestCount: 75,
    eventDuration: 6,
    createdAt: daysAgo(7),
    updatedAt: daysAgo(5),
    updatedBy: demoUser.id,
    lineItems: [],
    paymentSchedules: [],
  },
]

// ============================================================================
// Demo Tasks
// ============================================================================

export const demoTasks = [
  {
    id: 'task-001',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_INQUIRY,
    parentId: 'inquiry-002',
    title: 'Follow up on Chen wedding menu selection',
    description: 'Send final menu options and get confirmation',
    taskStatus: TaskStatusEnum.TASK_STATUS_NOT_STARTED,
    dueDate: daysFromNow(3),
    assignedTo: demoUser.id,
    assignedToFirstName: demoUser.firstName,
    assignedToLastName: demoUser.lastName,
    createdAt: daysAgo(2),
    updatedAt: daysAgo(1),
    createdBy: demoUser.id,
    createdByFirstName: demoUser.firstName,
    createdByLastName: demoUser.lastName,
  },
  {
    id: 'task-002',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_BOOKING,
    parentId: 'booking-001',
    title: 'Confirm staffing for Wilson party',
    description: 'Verify all 3 servers confirmed for the event',
    taskStatus: TaskStatusEnum.TASK_STATUS_IN_PROGRESS,
    dueDate: daysFromNow(7),
    assignedTo: demoUser.id,
    assignedToFirstName: demoUser.firstName,
    assignedToLastName: demoUser.lastName,
    createdAt: daysAgo(5),
    updatedAt: daysAgo(1),
    createdBy: demoUser.id,
    createdByFirstName: demoUser.firstName,
    createdByLastName: demoUser.lastName,
  },
  {
    id: 'task-003',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_INQUIRY,
    parentId: 'inquiry-001',
    title: 'Send proposal to Tech Ventures new lead',
    description: 'Prepare and send initial quote',
    taskStatus: TaskStatusEnum.TASK_STATUS_NOT_STARTED,
    dueDate: daysFromNow(1),
    assignedTo: demoUser.id,
    assignedToFirstName: demoUser.firstName,
    assignedToLastName: demoUser.lastName,
    createdAt: daysAgo(1),
    updatedAt: daysAgo(0),
    createdBy: demoUser.id,
    createdByFirstName: demoUser.firstName,
    createdByLastName: demoUser.lastName,
  },
  {
    id: 'task-004',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_BOOKING,
    parentId: 'booking-004',
    title: 'Order supplies for Innovate Corp event',
    description: 'Place orders with vendors',
    taskStatus: TaskStatusEnum.TASK_STATUS_NOT_STARTED,
    dueDate: daysFromNow(14),
    assignedTo: demoUser.id,
    assignedToFirstName: demoUser.firstName,
    assignedToLastName: demoUser.lastName,
    createdAt: daysAgo(3),
    updatedAt: daysAgo(3),
    createdBy: demoUser.id,
    createdByFirstName: demoUser.firstName,
    createdByLastName: demoUser.lastName,
  },
  {
    id: 'task-005',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_BOOKING,
    parentId: 'booking-001',
    title: 'Collect final payment from Wilson',
    description: 'Send payment reminder before event',
    taskStatus: TaskStatusEnum.TASK_STATUS_NOT_STARTED,
    dueDate: daysFromNow(5),
    assignedTo: demoUser.id,
    assignedToFirstName: demoUser.firstName,
    assignedToLastName: demoUser.lastName,
    createdAt: daysAgo(7),
    updatedAt: daysAgo(7),
    createdBy: demoUser.id,
    createdByFirstName: demoUser.firstName,
    createdByLastName: demoUser.lastName,
  },
  {
    id: 'task-006',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_INQUIRY,
    parentId: 'inquiry-003',
    title: 'Review Innovate Corp quote feedback',
    description: 'Client requested changes to menu selection',
    taskStatus: TaskStatusEnum.TASK_STATUS_IN_PROGRESS,
    dueDate: daysFromNow(2),
    assignedTo: demoUser.id,
    assignedToFirstName: demoUser.firstName,
    assignedToLastName: demoUser.lastName,
    createdAt: daysAgo(4),
    updatedAt: daysAgo(1),
    createdBy: demoUser.id,
    createdByFirstName: demoUser.firstName,
    createdByLastName: demoUser.lastName,
  },
  {
    id: 'task-007',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_BOOKING,
    parentId: 'booking-002',
    title: 'Finalize wedding timeline',
    description: 'Coordinate with venue and client on schedule',
    taskStatus: TaskStatusEnum.TASK_STATUS_IN_PROGRESS,
    dueDate: daysFromNow(4),
    assignedTo: demoUser.id,
    assignedToFirstName: demoUser.firstName,
    assignedToLastName: demoUser.lastName,
    createdAt: daysAgo(8),
    updatedAt: daysAgo(2),
    createdBy: demoUser.id,
    createdByFirstName: demoUser.firstName,
    createdByLastName: demoUser.lastName,
  },
  {
    id: 'task-008',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_INQUIRY,
    parentId: 'inquiry-007',
    title: 'Schedule site visit for Corporate Events Hall',
    description: 'Visit venue to assess setup requirements',
    taskStatus: TaskStatusEnum.TASK_STATUS_NOT_STARTED,
    dueDate: daysFromNow(6),
    assignedTo: demoUser.id,
    assignedToFirstName: demoUser.firstName,
    assignedToLastName: demoUser.lastName,
    createdAt: daysAgo(3),
    updatedAt: daysAgo(3),
    createdBy: demoUser.id,
    createdByFirstName: demoUser.firstName,
    createdByLastName: demoUser.lastName,
  },
  {
    id: 'task-009',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_BOOKING,
    parentId: 'booking-003',
    title: 'Confirm wedding menu with client',
    description: 'Get final approval on menu selections',
    taskStatus: TaskStatusEnum.TASK_STATUS_NOT_STARTED,
    dueDate: daysFromNow(10),
    assignedTo: demoUser.id,
    assignedToFirstName: demoUser.firstName,
    assignedToLastName: demoUser.lastName,
    createdAt: daysAgo(2),
    updatedAt: daysAgo(2),
    createdBy: demoUser.id,
    createdByFirstName: demoUser.firstName,
    createdByLastName: demoUser.lastName,
  },
  {
    id: 'task-010',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_INQUIRY,
    parentId: 'inquiry-011',
    title: 'Prepare quote for Family Events',
    description: 'Create detailed quote for anniversary party',
    taskStatus: TaskStatusEnum.TASK_STATUS_NOT_STARTED,
    dueDate: daysFromNow(8),
    assignedTo: demoUser.id,
    assignedToFirstName: demoUser.firstName,
    assignedToLastName: demoUser.lastName,
    createdAt: daysAgo(5),
    updatedAt: daysAgo(5),
    createdBy: demoUser.id,
    createdByFirstName: demoUser.firstName,
    createdByLastName: demoUser.lastName,
  },
  {
    id: 'task-011',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_BOOKING,
    parentId: 'booking-001',
    title: 'Setup equipment for Wilson party',
    description: 'Deliver and setup all catering equipment',
    taskStatus: TaskStatusEnum.TASK_STATUS_COMPLETED,
    dueDate: daysAgo(1),
    assignedTo: demoUser.id,
    assignedToFirstName: demoUser.firstName,
    assignedToLastName: demoUser.lastName,
    createdAt: daysAgo(10),
    updatedAt: daysAgo(1),
    createdBy: demoUser.id,
    createdByFirstName: demoUser.firstName,
    createdByLastName: demoUser.lastName,
  },
  {
    id: 'task-012',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_INQUIRY,
    parentId: 'inquiry-012',
    title: 'Follow up on Corporate HQ inquiry',
    description: 'Call to discuss event details and requirements',
    taskStatus: TaskStatusEnum.TASK_STATUS_NOT_STARTED,
    dueDate: daysFromNow(0),
    assignedTo: demoUser.id,
    assignedToFirstName: demoUser.firstName,
    assignedToLastName: demoUser.lastName,
    createdAt: daysAgo(0),
    updatedAt: daysAgo(0),
    createdBy: demoUser.id,
    createdByFirstName: demoUser.firstName,
    createdByLastName: demoUser.lastName,
  },
]

// ============================================================================
// Demo Notes
// ============================================================================

export const demoNotes = [
  {
    id: 'note-001',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_INQUIRY,
    parentId: 'inquiry-002',
    note: 'Client is very interested in vegetarian options. Need to include more plant-based menu items in the quote.',
    createdAt: daysAgo(12),
    createdBy: demoUser.id,
    updatedAt: daysAgo(12),
    updatedBy: demoUser.id,
  },
  {
    id: 'note-002',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_BOOKING,
    parentId: 'booking-001',
    note: 'Client confirmed final guest count of 100. All dietary restrictions have been noted.',
    createdAt: daysAgo(20),
    createdBy: demoUser.id,
    updatedAt: daysAgo(20),
    updatedBy: demoUser.id,
  },
  {
    id: 'note-003',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_INQUIRY,
    parentId: 'inquiry-003',
    note: 'Followed up with client via email. They are reviewing the quote and will get back to us by end of week.',
    createdAt: daysAgo(5),
    createdBy: demoUser.id,
    updatedAt: daysAgo(5),
    updatedBy: demoUser.id,
  },
  {
    id: 'note-004',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_BOOKING,
    parentId: 'booking-002',
    note: 'Venue requires setup to begin at 8 AM. Need to coordinate with staff arrival time.',
    createdAt: daysAgo(30),
    createdBy: demoUser.id,
    updatedAt: daysAgo(30),
    updatedBy: demoUser.id,
  },
  {
    id: 'note-005',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_INQUIRY,
    parentId: 'inquiry-007',
    note: 'Client mentioned they have a tight budget. Consider offering package deals or discounts.',
    createdAt: daysAgo(8),
    createdBy: demoUser.id,
    updatedAt: daysAgo(8),
    updatedBy: demoUser.id,
  },
  {
    id: 'note-006',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_BOOKING,
    parentId: 'booking-003',
    note: 'Wedding planner is very organized. All communication has been smooth so far.',
    createdAt: daysAgo(6),
    createdBy: demoUser.id,
    updatedAt: daysAgo(6),
    updatedBy: demoUser.id,
  },
  {
    id: 'note-007',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_INQUIRY,
    parentId: 'inquiry-011',
    note: 'Client is planning a milestone anniversary celebration. Wants something elegant and memorable.',
    createdAt: daysAgo(15),
    createdBy: demoUser.id,
    updatedAt: daysAgo(15),
    updatedBy: demoUser.id,
  },
  {
    id: 'note-008',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_BOOKING,
    parentId: 'booking-004',
    note: 'Event completed successfully. Client was very happy with the service. Received positive feedback.',
    createdAt: daysAgo(2),
    createdBy: demoUser.id,
    updatedAt: daysAgo(2),
    updatedBy: demoUser.id,
  },
  {
    id: 'note-009',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_INQUIRY,
    parentId: 'inquiry-012',
    note: 'Large corporate event with 200+ attendees. Will need significant staffing and equipment.',
    createdAt: daysAgo(0),
    createdBy: demoUser.id,
    updatedAt: daysAgo(0),
    updatedBy: demoUser.id,
  },
  {
    id: 'note-010',
    organizationId: demoOrganization.id,
    parentType: ParentTypeEnum.PARENT_TYPE_BOOKING,
    parentId: 'booking-001',
    note: 'Final payment reminder sent. Client confirmed they will pay by end of week.',
    createdAt: daysAgo(3),
    createdBy: demoUser.id,
    updatedAt: daysAgo(3),
    updatedBy: demoUser.id,
  },
]

// ============================================================================
// Demo Feedback
// ============================================================================

export const demoFeedback = [
  {
    id: 'feedback-001',
    organizationId: demoOrganization.id,
    userId: demoUser.id,
    feedback: 'It would be great to have a mobile app for managing events on the go. This would make it much easier to check event details while at the venue.',
    feedbackStage: FeedbackStageEnum.FEEDBACK_STAGE_NEW,
    upvotesCount: 5,
    hasUpvoted: false,
    createdAt: daysAgo(10),
    createdBy: demoUser.id,
    updatedAt: daysAgo(10),
    updatedBy: demoUser.id,
  },
  {
    id: 'feedback-002',
    organizationId: demoOrganization.id,
    userId: demoUser.id,
    feedback: 'The invoice PDF export feature is very helpful! Could we add the ability to customize the invoice template with our logo?',
    feedbackStage: FeedbackStageEnum.FEEDBACK_STAGE_PLANNED,
    upvotesCount: 8,
    hasUpvoted: true,
    createdAt: daysAgo(25),
    createdBy: demoUser.id,
    updatedAt: daysAgo(20),
    updatedBy: demoUser.id,
  },
  {
    id: 'feedback-003',
    organizationId: demoOrganization.id,
    userId: demoUser.id,
    feedback: 'Would love to see integration with Google Calendar so events automatically sync. This would save a lot of manual entry time.',
    feedbackStage: FeedbackStageEnum.FEEDBACK_STAGE_IN_PROGRESS,
    upvotesCount: 12,
    hasUpvoted: true,
    createdAt: daysAgo(40),
    createdBy: demoUser.id,
    updatedAt: daysAgo(15),
    updatedBy: demoUser.id,
  },
  {
    id: 'feedback-004',
    organizationId: demoOrganization.id,
    userId: demoUser.id,
    feedback: 'The task management feature is great! Could we add the ability to set recurring tasks for regular events?',
    feedbackStage: FeedbackStageEnum.FEEDBACK_STAGE_NEW,
    upvotesCount: 3,
    hasUpvoted: false,
    createdAt: daysAgo(5),
    createdBy: demoUser.id,
    updatedAt: daysAgo(5),
    updatedBy: demoUser.id,
  },
  {
    id: 'feedback-005',
    organizationId: demoOrganization.id,
    userId: demoUser.id,
    feedback: 'It would be helpful to have a client portal where customers can view their quotes and invoices, make payments, and update their event details.',
    feedbackStage: FeedbackStageEnum.FEEDBACK_STAGE_COMPLETE,
    upvotesCount: 15,
    hasUpvoted: true,
    createdAt: daysAgo(60),
    createdBy: demoUser.id,
    updatedAt: daysAgo(5),
    updatedBy: demoUser.id,
  },
  {
    id: 'feedback-006',
    organizationId: demoOrganization.id,
    userId: demoUser.id,
    feedback: 'The calendar view is perfect! Could we add the ability to filter by event type or status?',
    feedbackStage: FeedbackStageEnum.FEEDBACK_STAGE_NEW,
    upvotesCount: 2,
    hasUpvoted: false,
    createdAt: daysAgo(2),
    createdBy: demoUser.id,
    updatedAt: daysAgo(2),
    updatedBy: demoUser.id,
  },
]

// ============================================================================
// Demo Items (Catalog)
// ============================================================================

export const demoItems = [
  {
    id: 'item-001',
    organizationId: demoOrganization.id,
    title: 'Premium Catering Package',
    description: 'Full service catering with appetizers, main course, and dessert',
    price: 45.0,
    unit: 'ITEM_UNIT_PER_UNIT',
    alwaysInclude: false,
    includeOnForm: true,
    createdAt: daysAgo(300),
    updatedAt: daysAgo(30),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'item-002',
    organizationId: demoOrganization.id,
    title: 'Bar Service',
    description: 'Open bar with premium selections',
    price: 25.0,
    unit: 'ITEM_UNIT_PER_HOUR',
    alwaysInclude: false,
    includeOnForm: true,
    createdAt: daysAgo(300),
    updatedAt: daysAgo(30),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'item-003',
    organizationId: demoOrganization.id,
    title: 'Event Coordination',
    description: 'On-site event coordinator for the duration of your event',
    price: 200.0,
    unit: 'ITEM_UNIT_PER_EVENT',
    alwaysInclude: true,
    includeOnForm: false,
    createdAt: daysAgo(300),
    updatedAt: daysAgo(30),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'item-004',
    organizationId: demoOrganization.id,
    title: 'Wait Staff',
    description: 'Professional wait staff service',
    price: 35.0,
    unit: 'ITEM_UNIT_PER_HOUR',
    alwaysInclude: false,
    includeOnForm: true,
    createdAt: daysAgo(200),
    updatedAt: daysAgo(30),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'item-005',
    organizationId: demoOrganization.id,
    title: 'Dessert Station',
    description: 'Custom dessert station with assorted pastries and treats',
    price: 15.0,
    unit: 'ITEM_UNIT_PER_UNIT',
    alwaysInclude: false,
    includeOnForm: true,
    createdAt: daysAgo(250),
    updatedAt: daysAgo(25),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'item-006',
    organizationId: demoOrganization.id,
    title: 'Setup & Teardown',
    description: 'Complete setup before event and teardown after event',
    price: 150.0,
    unit: 'ITEM_UNIT_PER_EVENT',
    alwaysInclude: true,
    includeOnForm: false,
    createdAt: daysAgo(280),
    updatedAt: daysAgo(20),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'item-007',
    organizationId: demoOrganization.id,
    title: 'Bartender Service',
    description: 'Professional bartender for cocktail service',
    price: 40.0,
    unit: 'ITEM_UNIT_PER_HOUR',
    alwaysInclude: false,
    includeOnForm: true,
    createdAt: daysAgo(220),
    updatedAt: daysAgo(15),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'item-008',
    organizationId: demoOrganization.id,
    title: 'Equipment Rental',
    description: 'Tables, chairs, linens, and other event equipment',
    price: 500.0,
    unit: 'ITEM_UNIT_PER_DAY',
    alwaysInclude: false,
    includeOnForm: true,
    createdAt: daysAgo(190),
    updatedAt: daysAgo(10),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'item-009',
    organizationId: demoOrganization.id,
    title: 'Photography Package',
    description: 'Professional event photography services',
    price: 800.0,
    unit: 'ITEM_UNIT_PER_EVENT',
    alwaysInclude: false,
    includeOnForm: true,
    createdAt: daysAgo(160),
    updatedAt: daysAgo(5),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
]

// ============================================================================
// Demo Tax Rates
// ============================================================================

export const demoTaxRates = [
  {
    id: 'tax-001',
    organizationId: demoOrganization.id,
    taxType: 'Sales Tax',
    name: 'Texas Sales Tax',
    percentage: 8.25,
    createdAt: daysAgo(365),
    updatedAt: daysAgo(365),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
]

// ============================================================================
// Demo Discounts
// ============================================================================

export const demoDiscounts = [
  {
    id: 'discount-001',
    organizationId: demoOrganization.id,
    name: 'Early Bird',
    description: '10% off for bookings made 6+ months in advance',
    discountType: 'DISCOUNT_TYPE_PERCENTAGE',
    value: 10.0,
    isActive: true,
    createdAt: daysAgo(300),
    updatedAt: daysAgo(30),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
  {
    id: 'discount-002',
    organizationId: demoOrganization.id,
    name: 'Referral Discount',
    description: '$200 off for referred customers',
    discountType: 'DISCOUNT_TYPE_FLAT',
    value: 200.0,
    isActive: true,
    createdAt: daysAgo(300),
    updatedAt: daysAgo(30),
    createdBy: demoUser.id,
    updatedBy: demoUser.id,
  },
]

// ============================================================================
// Dashboard Stats
// ============================================================================

export const demoDashboardStats = {
  totalRevenue: 110465,
  revenueChange: 23.5,
  activeLeads: 13,
  leadsChange: 12,
  upcomingEvents: 9,
  eventsThisMonth: 4,
  openInvoices: 4,
  totalOutstanding: 3250,
}
